/**
 * GroupCollisionDialog — Feb 2026.
 *
 * Same-name disambiguation prompt. Shown when the Add Contact / Edit
 * Contact save path would default-rule into an EXISTING family group
 * (e.g. "Smith" already has 4 members). The user picks one of:
 *
 *   1. Same family — link to an existing candidate (radio per candidate;
 *      candidates show sample member names so the user can recognize them).
 *   2. New family — create a distinct group with a differentiator. The
 *      input is pre-filled with suggested_full_name (city → first_name).
 *      The user can edit the entire string freely ("Smith — lake house").
 *
 * The dialog OWNS the create-new-group POST so the caller only has to
 * deal with one onResolve(group_id) callback.
 *
 * Props
 *   open: boolean
 *   onOpenChange(open: boolean): void
 *   data: check-collision response
 *     { candidates, suggested_differentiator, suggested_full_name, base_name, type }
 *   onResolve(group_id: string): void   — called after pick / create
 *   onCancel(): void                    — user closed without resolving
 *
 * data-testids:
 *   group-collision-dialog
 *   group-collision-candidate-{id}
 *   group-collision-radio-new
 *   group-collision-new-name-input
 *   group-collision-confirm-btn
 *   group-collision-cancel-btn
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, Building2, Sparkles, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function memberSummary(members) {
  if (!Array.isArray(members) || members.length === 0) return 'no members yet';
  return members
    .map(m => [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || 'unnamed')
    .filter(Boolean)
    .join(', ');
}

export default function GroupCollisionDialog({ open, onOpenChange, data, onResolve, onCancel }) {
  // Initial choice: "new" by default so the user notices the alternative,
  // but if there's exactly one candidate AND it has high overlap we
  // still default to "new" — the whole point is to NOT silently fuse.
  const [choice, setChoice] = useState('new');
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && data) {
      setChoice('new');
      // Pre-fill with suggested_full_name; fall back to base_name + " — " if
      // no differentiator was suggested (caller must edit before confirm).
      setNewName(data.suggested_full_name || data.base_name || '');
    }
  }, [open, data]);

  if (!data) return null;

  const isFamily = data.type === 'family';
  const Icon = isFamily ? Users : Building2;
  const candidates = data.candidates || [];

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      if (choice === 'new') {
        const trimmed = (newName || '').trim();
        if (!trimmed) {
          toast.error('Please enter a distinct name for the new group');
          setSubmitting(false);
          return;
        }
        // Edge case: user typed back the exact base_name. The backend's
        // unique constraint would reject this. Nudge them to differentiate.
        if (trimmed.toLowerCase() === (data.base_name || '').toLowerCase()) {
          toast.error(`"${trimmed}" already exists in your tenant — please add a differentiator (city, member name, etc.)`);
          setSubmitting(false);
          return;
        }
        // Create the new group, then return its id.
        const res = await axios.post(
          `${API}/api/contact-groups`,
          { name: trimmed, type: data.type },
          { headers: authHeaders() },
        );
        onResolve(res.data.id);
      } else {
        // choice is a candidate id.
        onResolve(choice);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resolve the group choice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="group-collision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            A &ldquo;{data.base_name}&rdquo; {isFamily ? 'family' : 'company'} group already exists
          </DialogTitle>
          <DialogDescription>
            Is this contact part of an existing {isFamily ? 'family' : 'company'},
            or is this a different one with the same name?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 max-h-[55vh] overflow-y-auto py-1">
          {candidates.map(c => (
            <label
              key={c.id}
              htmlFor={`gcd-${c.id}`}
              className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-colors ${
                choice === c.id ? 'border-amber-400 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'
              }`}
              data-testid={`group-collision-candidate-${c.id}`}
            >
              <input
                id={`gcd-${c.id}`}
                type="radio"
                name="group-collision"
                value={c.id}
                checked={choice === c.id}
                onChange={() => setChoice(c.id)}
                className="mt-1"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{c.name}</span>
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {c.member_count || 0} member{c.member_count === 1 ? '' : 's'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  Members: {memberSummary(c.sample_members)}
                </p>
              </div>
            </label>
          ))}

          {/* "New group" option — always shown. */}
          <label
            htmlFor="gcd-new"
            className={`flex items-start gap-2.5 p-2.5 rounded-md border cursor-pointer transition-colors ${
              choice === 'new' ? 'border-emerald-400 bg-emerald-50' : 'border-stone-200 hover:bg-stone-50'
            }`}
            data-testid="group-collision-radio-new"
          >
            <input
              id="gcd-new"
              type="radio"
              name="group-collision"
              value="new"
              checked={choice === 'new'}
              onChange={() => setChoice('new')}
              className="mt-1"
            />
            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-center gap-2">
                <Plus className="w-3.5 h-3.5 text-emerald-700" />
                <span className="font-medium text-sm text-emerald-900">
                  New {isFamily ? 'family' : 'company'} (different from the above)
                </span>
              </div>
              <div className="space-y-1">
                <Label htmlFor="gcd-new-name" className="text-[11px] text-muted-foreground">
                  Group name — edit freely to make it recognizable:
                </Label>
                <Input
                  id="gcd-new-name"
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setChoice('new'); }}
                  placeholder={`e.g. ${data.base_name} — lake house`}
                  className="text-sm"
                  data-testid="group-collision-new-name-input"
                />
                {data.suggested_differentiator && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Auto-suggested from {data.suggested_source === 'city' ? 'address city' : 'first name'}.
                  </p>
                )}
              </div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { onCancel?.(); onOpenChange(false); }}
            data-testid="group-collision-cancel-btn"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="group-collision-confirm-btn"
          >
            {submitting ? 'Saving…' : 'Confirm choice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
